import { GridCell } from "./GridCell";
import { relateCrops, strongestRelation, orthogonalNeighbors } from "./companion-utils";

/**
 * Pure layout — no data fetching, no DnD state. Receives the full occupancy
 * payload from its orchestrator, plus the optional `activeDrag` so empty
 * cells can preview companion/antagonist hints while a chip is being dragged.
 */
export function BedGrid({ occupancy, activeDrag, onEditCrop, onDeleteCrop }) {
    if (!occupancy) return null;
    const { grid_rows, grid_cols, cells, crops } = occupancy;

    // For each crop, find its anchor cell so we only render the chip once.
    const anchorByCrop = {};
    Object.values(crops || {}).forEach(c => {
        if (!c.position) return;
        anchorByCrop[c.id] = { row: c.position.row, col: c.position.col };
    });

    // Normalise cell entries — backend now returns lists of crop ids per
    // cell (companion sharing), but legacy single-id payloads still occur
    // in some cached responses. `cellIds(r, c)` always yields an array.
    const cellIds = (r, c) => {
        const v = cells?.[r]?.[c];
        if (Array.isArray(v)) return v;
        return v ? [v] : [];
    };

    // Pre-compute drag hint per cell when something is being dragged. Only
    // hint EMPTY cells (occupied cells already have their own chip warnings).
    let cellHints = null;
    if (activeDrag) {
        cellHints = Array.from({ length: grid_rows }, () => Array(grid_cols).fill("neutral"));
        for (let r = 0; r < grid_rows; r++) {
            for (let c = 0; c < grid_cols; c++) {
                if (cellIds(r, c).length) continue; // skip occupied
                const neighbours = orthogonalNeighbors(r + 1, c + 1, grid_rows, grid_cols);
                const rels = neighbours
                    .flatMap(([nr, nc]) => cellIds(nr - 1, nc - 1))
                    .filter((id) => id !== activeDrag.id) // ignore dragged-self
                    .map((id) => relateCrops(activeDrag, crops[id]));
                cellHints[r][c] = strongestRelation(rels);
            }
        }
    }

    return (
        <div
            data-testid="bed-grid"
            className="grid gap-1.5 p-4 rounded-lg bg-[var(--vm-input)]/30 border border-dashed border-[var(--vm-border)] overflow-x-auto"
            style={{
                gridTemplateColumns: `repeat(${grid_cols}, minmax(56px, 1fr))`,
                gridTemplateRows: `repeat(${grid_rows}, minmax(48px, auto))`,
            }}
        >
            {/* Render cells row-major */}
            {Array.from({ length: grid_rows }).flatMap((_, r) =>
                Array.from({ length: grid_cols }).map((_, c) => {
                    const row = r + 1;
                    const col = c + 1;
                    const ids = cellIds(r, c);
                    // Primary occupant is the first crop whose anchor sits
                    // in this cell (so spans render their chip once),
                    // falling back to the first id present.
                    const primaryId = ids.find((id) => {
                        const a = anchorByCrop[id];
                        return a && a.row === row && a.col === col;
                    }) || ids[0] || null;
                    const cropId = primaryId;
                    const crop = cropId ? crops[cropId] : null;
                    const anchor = cropId ? anchorByCrop[cropId] : null;
                    const isAnchor = !!anchor && anchor.row === row && anchor.col === col;
                    const companionIds = ids.filter((id) => id !== cropId);
                    return (
                        <GridCell
                            key={`${row}-${col}`}
                            row={row}
                            col={col}
                            cropId={cropId}
                            crop={crop}
                            companions={companionIds.map((id) => crops[id]).filter(Boolean)}
                            isAnchor={isAnchor}
                            dragHint={cellHints?.[r]?.[c] ?? "neutral"}
                            isDragActive={!!activeDrag}
                            onEditCrop={onEditCrop}
                            onDeleteCrop={onDeleteCrop}
                        />
                    );
                })
            )}
        </div>
    );
}
